import clsx from "clsx";
import { useCallback, useMemo, useState } from "react";
import { UserJobsList } from "./UserJobsList";
import userJobApi from "@/api/userJob";
import { Button } from "@/components/Button/Button";
import { SmartMatchIcon } from "@/components/Icons/Icons";
import { JobActionsMenu } from "@/components/Jobs/JobActionsMenu";
import { JobStatusSelector } from "@/components/Jobs/JobStatusSelector";
import { JobTitleCell, TableCell } from "@/components/Jobs/JobTableCells";
import { UserJobsEmptyState } from "@/components/Jobs/JobsEmptyState";
import { Pagination } from "@/components/Pagination/Pagination";
import { AppText } from "@/components/Text/AppText";
import { useAppDispatch, useAppSelector } from "@/hooks/types";
import { useInvalidateUserJobsCache } from "@/hooks/useAppData";
import { closeModal, openModal } from "@/store/modal/slice";
import {
    getArchivedUserJobs,
    getNonArchivedUserJobs,
} from "@/store/userJob/selectors";
import { deleteUserJob, updateUserJob } from "@/store/userJob/slice";
import { ModalTypes } from "@/types/modal";
import { IUserJob } from "@/types/userJob";

const dashboardColumns = ["title", "status", "location", "date", "actions"];

const archivedColumns = ["title", "status", "location", "date", "actions"];

const jobSelectColumns = ["title", "status", "location", "date", "select"];

export const UserJobsTable = ({
    showArchived,
    onJobSelect,
    activeJob,
}: {
    showArchived: boolean;
    onJobSelect?: (job: IUserJob) => void;
    activeJob?: IUserJob;
}) => {
    const dispatch = useAppDispatch();
    const invalidateUserJobsCache = useInvalidateUserJobsCache();
    const [isLoadingActions, setIsLoadingActions] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Get jobs from store
    const nonArchivedJobs = useAppSelector(getNonArchivedUserJobs);
    const archivedUserJobs = useAppSelector(getArchivedUserJobs);
    const allUserJobs = showArchived ? archivedUserJobs : nonArchivedJobs;

    const tableMode = useMemo(() => {
        if (onJobSelect) {
            return "jobSelect";
        }
        if (showArchived) {
            return "archived";
        }
        return "all";
    }, [onJobSelect, showArchived]);

    // Calculate pagination
    const totalResults = allUserJobs.length;
    const totalPages = Math.ceil(totalResults / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const userJobs = allUserJobs.slice(startIndex, endIndex);

    // Reset to first page when switching between archived/non-archived
    useMemo(() => {
        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, totalPages, showArchived]);

    const onPageChange = useCallback((page: number) => {
        setCurrentPage(page);
        // Scroll to top when page changes
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, []);

    const canRenderColumn = useCallback(
        (column: string) => {
            if (tableMode === "jobSelect") {
                return jobSelectColumns.includes(column);
            }
            if (tableMode === "archived") {
                return archivedColumns.includes(column);
            }
            return dashboardColumns.includes(column);
        },
        [tableMode],
    );

    const onCreateJob = useCallback(() => {
        dispatch(
            openModal({
                modalType: ModalTypes.MANAGE_USER_JOB,
                props: {
                    userJobId: null,
                },
            }),
        );
    }, [dispatch]);

    const openJobResumesModal = useCallback(
        (job: IUserJob) => {
            dispatch(
                openModal({
                    modalType: ModalTypes.USER_JOB_RESUMES,
                    props: { activeJob: job },
                }),
            );
        },
        [dispatch],
    );

    const onEditJob = useCallback(
        (userJob: IUserJob) => {
            dispatch(
                openModal({
                    modalType: ModalTypes.MANAGE_USER_JOB,
                    props: { userJobId: userJob.id },
                }),
            );
        },
        [dispatch],
    );

    const onQuickUpdateJob = useCallback(
        async (job: IUserJob, newFields: Partial<IUserJob>) => {
            try {
                setIsLoadingActions(true);
                const updatedJob: IUserJob = {
                    ...job,
                    ...newFields,
                };
                await userJobApi.saveUserJob(updatedJob, job.id);
                dispatch(updateUserJob(updatedJob));
                // Invalidate cache to refresh data from server
                invalidateUserJobsCache();
            } catch (error) {
                console.error(error);
            } finally {
                setIsLoadingActions(false);
            }
        },
        [dispatch, invalidateUserJobsCache],
    );

    const handleDeleteJob = useCallback(
        async (job: IUserJob) => {
            dispatch(
                openModal({
                    modalType: ModalTypes.CONFIRM,
                    props: {
                        title: "Delete Job",
                        message: "Are you sure you want to delete this job?",
                        onConfirm: async () => {
                            await userJobApi.deleteUserJob(job.id);
                            dispatch(deleteUserJob(job.id));
                            dispatch(closeModal(ModalTypes.CONFIRM));
                            // Invalidate cache to refresh data from server
                            invalidateUserJobsCache();
                        },
                    },
                }),
            );
        },
        [dispatch, invalidateUserJobsCache],
    );

    const openSmartMatchModal = useCallback(
        (job: IUserJob) => {
            dispatch(
                openModal({
                    modalType: ModalTypes.SMART_MATCH,
                    props: { userJobId: job.id },
                }),
            );
        },
        [dispatch],
    );

    return (
        <>
            {/* Desktop View */}
            <div className="hidden w-full overflow-x-auto lg:block">
                <table className="w-full">
                    <thead className="text-neutral-50">
                        <tr className="h-8 text-neutral-50">
                            <th className="w-[300px] rounded-tl-lg bg-primary-700 text-center">
                                <AppText variant="labelsbuttons">
                                    Job Title & Company
                                </AppText>
                            </th>
                            <th className="min-w-[120px] bg-primary-700 text-center">
                                <AppText variant="labelsbuttons">
                                    Status
                                </AppText>
                            </th>
                            <th className="min-w-[120px] bg-primary-700 text-center">
                                <AppText variant="labelsbuttons">
                                    Location
                                </AppText>
                            </th>
                            <th className="min-w-[120px] bg-primary-700 text-center">
                                <AppText variant="labelsbuttons">Date</AppText>
                            </th>
                            {canRenderColumn("actions") && (
                                <th className="min-w-[100px] rounded-tr-lg  bg-primary-700 text-center"></th>
                            )}
                            {canRenderColumn("select") && (
                                <th className="min-w-[100px] rounded-tr-lg  bg-primary-700 text-center">
                                    <AppText variant="labelsbuttons">
                                        Selected
                                    </AppText>
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody className="rounded-b-lg border-b border-l border-r border-neutral-200 bg-neutral-50">
                        {userJobs.length === 0 ? (
                            <tr className="last:rounded-b-lg">
                                <td
                                    colSpan={showArchived ? 5 : 5}
                                    className="rounded-b-lg p-4"
                                >
                                    <UserJobsEmptyState
                                        showArchived={showArchived}
                                        onCreateJob={onCreateJob}
                                    />
                                </td>
                            </tr>
                        ) : (
                            userJobs.map((job, index, array) => (
                                <tr
                                    key={job.id}
                                    className={clsx(
                                        "bg-neutral-white h-[68px]",
                                        index === array.length - 1
                                            ? ""
                                            : "border-b border-neutral-200",
                                    )}
                                >
                                    <JobTitleCell
                                        title={job.jobTitle}
                                        organization={job.companyName || ""}
                                        isLast={index === array.length - 1}
                                        organizationLogo={null}
                                        width="w-[300px]"
                                    />

                                    <TableCell center>
                                        <JobStatusSelector
                                            status={job.status}
                                            onChange={status => {
                                                onQuickUpdateJob(job, {
                                                    status,
                                                });
                                            }}
                                            readOnly={
                                                showArchived || !!onJobSelect
                                            }
                                        />
                                    </TableCell>
                                    <TableCell center>
                                        <AppText
                                            variant="labelsbuttons"
                                            className="line-clamp-1 text-neutral-900"
                                        >
                                            {job.location || "—"}
                                        </AppText>
                                    </TableCell>
                                    <TableCell center>
                                        <AppText
                                            variant="labelsbuttons"
                                            className="line-clamp-1 text-neutral-900"
                                        >
                                            {job.createdAt
                                                ? new Date(
                                                      job.createdAt,
                                                  ).toLocaleDateString()
                                                : "—"}
                                        </AppText>
                                    </TableCell>
                                    {canRenderColumn("actions") && (
                                        <TableCell>
                                            <div className="flex items-center justify-center gap-2">
                                                {!showArchived && (
                                                    <Button
                                                        variant="outline"
                                                        color="gradient"
                                                        size="small"
                                                        className="!h-8 whitespace-nowrap"
                                                        onClick={() =>
                                                            openSmartMatchModal(
                                                                job,
                                                            )
                                                        }
                                                        leftIcon={
                                                            <SmartMatchIcon
                                                                gradient
                                                                className="h-7 w-7"
                                                            />
                                                        }
                                                    >
                                                        Tailor Resume
                                                    </Button>
                                                )}
                                                {/* {!showArchived &&
                                                    (job.linkedResumeIds
                                                        ?.length ? (
                                                        <Button
                                                            color="softIndigo"
                                                            className="!h-8 w-[170px] whitespace-nowrap"
                                                            onClick={() =>
                                                                openJobResumesModal(
                                                                    job,
                                                                )
                                                            }
                                                        >
                                                            View Resumes (
                                                            {
                                                                job
                                                                    .linkedResumeIds
                                                                    ?.length
                                                            }
                                                            )
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            color="primary"
                                                            className="!h-8 w-[170px] whitespace-nowrap"
                                                            onClick={() =>
                                                                openJobResumesModal(
                                                                    job,
                                                                )
                                                            }
                                                        >
                                                            Attach Resumes
                                                        </Button>
                                                    ))} */}
                                                <JobActionsMenu
                                                    job={job}
                                                    showArchived={showArchived}
                                                    onEditJob={onEditJob}
                                                    onArchiveJob={job => {
                                                        onQuickUpdateJob(job, {
                                                            archivedAt:
                                                                !job.archivedAt
                                                                    ? new Date().toISOString()
                                                                    : null,
                                                        });
                                                    }}
                                                    onDeleteJob={
                                                        handleDeleteJob
                                                    }
                                                    isLoading={isLoadingActions}
                                                    variant="desktop"
                                                />
                                            </div>
                                        </TableCell>
                                    )}
                                    {canRenderColumn("select") && (
                                        <TableCell center>
                                            <input
                                                className="h-4 w-4 cursor-pointer rounded-full border-2 border-primary-700 text-primary-700 focus:ring-transparent"
                                                type="radio"
                                                checked={
                                                    activeJob?.id === job.id
                                                }
                                                onChange={() =>
                                                    onJobSelect?.(job)
                                                }
                                            />
                                        </TableCell>
                                    )}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Mobile View */}
            <UserJobsList
                userJobs={userJobs}
                showArchived={showArchived}
                onCreateJob={onCreateJob}
                activeJob={activeJob}
                onJobSelect={onJobSelect}
                onEditJob={onEditJob}
                onQuickUpdateJob={onQuickUpdateJob}
                openSmartMatchModal={openSmartMatchModal}
                canRenderColumn={canRenderColumn}
                handleDeleteJob={handleDeleteJob}
                openJobResumesModal={openJobResumesModal}
            />

            {/* Pagination */}
            {totalPages > 1 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={onPageChange}
                    variant="minimal"
                    maxVisiblePages={5}
                    className="justify-center"
                />
            )}
        </>
    );
};
